import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/customers/token/refresh';
const HTTP_METHOD = 'post';

export type MakeRefreshCustomerTokenDeps<CustomerTokenReadSchema extends Schema.Top> = {
  readonly customerTokenRead: CustomerTokenReadSchema;
};

export const makeRefreshCustomerToken = <CustomerTokenReadSchema extends Schema.Top>(
  deps: MakeRefreshCustomerTokenDeps<CustomerTokenReadSchema>
) => {
  const RefreshCustomerTokenError = createBackendApiError({
    domain: 'CustomerTokenRefresh',
    endpoint: 'Refresh',
    path: ENDPOINT,
    identifierField: 'refresh_token',
  });

  const postRefreshTokenFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
    kind: 'client',
    attributes: {
      'http.request.method': HTTP_METHOD,
    },
  })(function* (refreshToken: string) {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/json', 'application/json')
      .fetch({ body: { refresh_token: refreshToken } });
  });

  const refreshCustomerToken = (refreshToken: string) =>
    postRefreshTokenFetch(refreshToken).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.customerTokenRead)(response),
          401: (response) =>
            Effect.fail(
              new RefreshCustomerTokenError.UnauthorizedError({
                refresh_token: refreshToken,
                response,
              })
            ),
          orElse: (response) =>
            Effect.fail(
              new RefreshCustomerTokenError.ApiRequestError({
                refresh_token: refreshToken,
                response,
              })
            ),
        })
      ),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.CustomerTokenRefresh,
          'Debug',
          'Successfully refreshed customer token'
        )
      ),
      Effect.withSpan('api.customer.refreshToken', {
        kind: 'internal',
      })
    );

  return { RefreshCustomerTokenError, refreshCustomerToken };
};
